import React, { useState, useEffect } from 'react';
import { getCurrentLocation } from '../utils/location';

/**
 * LocationSelector component
 * Allows users to share their location or enter a pincode manually
 * Calls onLocationChange when location is determined
 */
const LocationSelector = ({ onLocationChange, initialPincode }) => {
  const [locationStatus, setLocationStatus] = useState('idle'); // idle, loading, success, error
  const [locationError, setLocationError] = useState(null);
  const [manualPincode, setManualPincode] = useState(initialPincode || '');
  const [useManual, setUseManual] = useState(false);

  const requestLocation = async () => {
    setLocationStatus('loading');
    setLocationError(null);

    try {
      const location = await getCurrentLocation();
      setLocationStatus('success');
      onLocationChange({ type: 'coords', ...location });
    } catch (error) {
      setLocationStatus('error');
      setLocationError(error.message);
      setUseManual(true);
    }
  };

  const handleManualSubmit = () => {
    if (manualPincode && manualPincode.length >= 6) {
      onLocationChange({ type: 'pincode', pincode: manualPincode });
      setLocationStatus('success');
    }
  };

  useEffect(() => {
    if (initialPincode) {
      setManualPincode(initialPincode);
      setUseManual(true);
    }
  }, [initialPincode]);

  if (locationStatus === 'success' && !useManual) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, fontSize: 14 }}>
        <span style={{ color: '#16a34a' }}>✓</span>
        <span style={{ color: '#15803d' }}>Location detected</span>
        <button
          onClick={() => { setLocationStatus('idle'); setUseManual(true); }}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#15803d', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
        >
          Use pincode instead
        </button>
      </div>
    );
  }

  if (useManual || locationStatus === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Enter your pincode"
            value={manualPincode}
            onChange={(e) => setManualPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 8,
              fontSize: 14,
            }}
          />
          <button
            onClick={handleManualSubmit}
            disabled={!manualPincode || manualPincode.length < 6}
            className="btn-primary-sm"
            style={{ padding: '8px 16px', fontSize: 13 }}
          >
            Apply
          </button>
        </div>
        {locationError && (
          <p style={{ margin: 0, fontSize: 12, color: '#dc2626' }}>
            {locationError}
          </p>
        )}
        {!locationError && locationStatus === 'idle' && (
          <button
            onClick={requestLocation}
            style={{
              background: 'none',
              border: 'none',
              color: '#4f46e5',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: 13,
              textAlign: 'left',
              padding: 0,
            }}
          >
            Or use my current location
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <button
        onClick={requestLocation}
        disabled={locationStatus === 'loading'}
        className="btn-primary"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '10px 16px',
        }}
      >
        {locationStatus === 'loading' ? (
          <>
            <span>Detecting location...</span>
          </>
        ) : (
          <>
            <span>📍</span>
            <span>Use my current location</span>
          </>
        )}
      </button>
      <button
        onClick={() => setUseManual(true)}
        style={{
          background: 'none',
          border: 'none',
          color: '#6b7280',
          cursor: 'pointer',
          textDecoration: 'underline',
          fontSize: 13,
        }}
      >
        Or enter pincode manually
      </button>
    </div>
  );
};

export default LocationSelector;

